import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import {
  ImageMimeType,
  MediaType,
  VideoMimeType,
} from '../../../../../core/enums';
import { BehaviorSubject } from 'rxjs';
import { LingFile } from '../../../../../core/models/ling-file';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ling-media-slider',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './media-slider.component.html',
  styleUrls: ['./media-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaSliderComponent implements OnInit {
  @ViewChildren('mediaElement') mediaElements?: QueryList<ElementRef>;
  @ViewChild('scrollContainer') scrollableContainer!: ElementRef;

  @Input() set media(
    media: ({ _id?: string; file?: LingFile; isProcessing?: boolean } | File)[] | undefined
  ) {
    if (!media) {
      this.media$.next([]);
    } else {
      const playableMimeTypes = [
        ImageMimeType.JPEG,
        ImageMimeType.PNG,
        VideoMimeType.MP4,
      ];

      const newArray = media.map((mediaItem) => ({
        type: this.getTypeOfMedia(mediaItem)!,
        src: this.getMediaLink(mediaItem)!,
        isProcessing: mediaItem instanceof File ? false : mediaItem.isProcessing,
        needsProcessing: !playableMimeTypes.includes(
          mediaItem instanceof File ? mediaItem.type as any : (mediaItem.file?.mimetype as any)
        ),
      }));

      const existingArray = this.media$.value;

      if (newArray.length !== existingArray.length) {
        this.media$.next(newArray);
        return;
      }

      if (
        !newArray.every((newMediaItem) =>
          existingArray.some(
            (existingMediaItem) =>
              existingMediaItem.src === newMediaItem.src &&
              existingMediaItem.type === newMediaItem.type
          )
        )
      ) {
        this.media$.next(newArray);
      }
    }
  }

  media$ = new BehaviorSubject<
    {
      type?: MediaType;
      src?: SafeUrl;
      isProcessing?: boolean;
      needsProcessing: boolean;
    }[]
  >([]);

  @Input() videoActive = false;
  @Input() loginProcessMessage = false;
  videoMuted = true;
  showPauseAnimations = false;

  @Input() itemClickCB?: () => any;
  MediaType = MediaType;
  swipeCoords: [number, number] = [0, 0];
  swipeTime: number = 0;
  currentMediaIndex$ = new BehaviorSubject(0);
  previousMediaIndex = 0;

  get canScrollRight(): boolean {
    return (
      !!this.media$.value.length &&
      this.currentMediaIndex$.value < this.media$.value.length - 1
    );
  }

  get canScrollLeft(): boolean {
    return !!this.media$.value.length && this.currentMediaIndex$.value > 0;
  }

  get mediaItems(): ElementRef[] | undefined {
    return this.mediaElements?.toArray();
  }

  constructor(
    private sanitizer: DomSanitizer,
    public cd: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.currentMediaIndex$.subscribe((index) => {
      this.showPauseAnimations = false;
      const currentVideoElement = this.getVideoElement(index);
      const prevVideoElement = this.getVideoElement(this.previousMediaIndex);
      if (this.videoActive) {
        if (prevVideoElement) {
          prevVideoElement.pause();
          prevVideoElement.muted = this.videoMuted;
        }
        if (currentVideoElement) {
          currentVideoElement.muted = this.videoMuted;
          currentVideoElement.load();
          currentVideoElement.play().then();
        }
      }
      this.scrollableContainer?.nativeElement.scrollTo({
        left: this.mediaItems?.[index]?.nativeElement.offsetLeft,
      });
      this.previousMediaIndex = index;
    });
  }

  videoCanBePlayed(): boolean {
    const currentFile = this.media$.value[this.currentMediaIndex$.value];
    return (
      currentFile?.type === MediaType.Video &&
      this.videoActive &&
      !currentFile.isProcessing &&
      !currentFile.needsProcessing
    );
  }

  getVideoElement(index: number): HTMLVideoElement | null {
    const mediaContainerElement = this.mediaElements?.toArray().at(index);
    return mediaContainerElement?.nativeElement.querySelector('video');
  }

  getMediaLink(
    media: { _id?: string; file?: LingFile } | File
  ): string | SafeUrl | null {
    if (!media) {
      return null;
    }
    if (media instanceof File) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        URL.createObjectURL(media)
      );
    }
    if (media.file?.filename) {
      return environment.url + '/files/' + media.file.filename;
    }

    return null;
  }

  getTypeOfMedia(
    media: { _id?: string; file?: LingFile } | File
  ): MediaType | null {
    if (!media) {
      return null;
    }

    if (media instanceof File) {
      return (media.type?.split('/')[0] as MediaType) ?? null;
    }

    if (media.file?.mimetype) {
      return (media.file.mimetype.split('/')[0] as MediaType) ?? null;
    }

    return null;
  }

  onNextMedia() {
    if (this.canScrollRight) {
      this.currentMediaIndex$.next(this.currentMediaIndex$.value + 1);
    }
  }

  onPrevMedia() {
    if (this.canScrollLeft) {
      this.currentMediaIndex$.next(this.currentMediaIndex$.value - 1);
    }
  }

  onSoundToggle(index: number): void {
    const videoElement = this.getVideoElement(index);
    if (videoElement) {
      videoElement.muted = !videoElement.muted;
      this.videoMuted = videoElement.muted;
    }
  }

  onCentralClick(index?: number): void {
    if (this.itemClickCB) {
      this.itemClickCB();
      return;
    }
    if (this.videoActive && index !== undefined) {
      const videoElement = this.getVideoElement(index);
      if (videoElement) {
        this.showPauseAnimations = true;
        if (videoElement.paused) {
          videoElement.play().then();
        } else {
          videoElement.pause();
        }
      }
    }
  }

  goFullscreen(index: number): void {
    const videoElement = this.getVideoElement(index);
    if (videoElement) {
      const onPermissionFailed = () => {
        alert(
          this.translateService.instant('snackbar.warn.fullscreenNotAllowed')
        );
      };
      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen().catch(onPermissionFailed);
      } else if ((videoElement as any).mozRequestFullScreen) {
        (videoElement as any).mozRequestFullScreen().catch(onPermissionFailed);
      } else if ((videoElement as any).webkitRequestFullscreen) {
        (videoElement as any)
          .webkitRequestFullscreen()
          .catch(onPermissionFailed);
      } else if ((videoElement as any).msRequestFullscreen) {
        (videoElement as any).msRequestFullscreen().catch(onPermissionFailed);
      }
    }
  }

  // Have to enable controls in fullscreen mode for Firefox fullscreen controls to be shown
  onFullScreen(event: any): void {
    const videoElement = event.target as HTMLVideoElement;
    videoElement.controls = !!document.fullscreenElement;
  }

  swipeHandler(e: TouchEvent, when: string): void {
    const coords: [number, number] = [
      e.changedTouches[0].clientX,
      e.changedTouches[0].clientY,
    ];
    const time = new Date().getTime();

    if (when === 'start') {
      this.swipeCoords = coords;
      this.swipeTime = time;
    } else if (when === 'end') {
      const direction = [
        coords[0] - this.swipeCoords[0],
        coords[1] - this.swipeCoords[1],
      ];
      const duration = time - this.swipeTime;

      if (
        duration < 1000 &&
        Math.abs(direction[0]) > 30 && // Long enough
        Math.abs(direction[0]) > Math.abs(direction[1] * 3) // Horizontal enough
      ) {
        const swipe = direction[0] < 0 ? 'left' : 'right';
        if (swipe === 'left') {
          this.onNextMedia();
        }
        if (swipe === 'right') {
          this.onPrevMedia();
        }
      }
    }
  }
}
